import React, { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Alert, AlertDescription } from "../../../ui/alert";
import { Card, CardContent } from "../../../ui/card";
import { Upload, FileText } from "lucide-react";
import { FlowData } from "../utils/types";

interface ImportFlowModalProps {
  visible: boolean;
  onCancel: () => void;
  onImport: (flowData: FlowData) => void;
}

const ImportFlowModal: React.FC<ImportFlowModalProps> = ({
  visible,
  onCancel,
  onImport,
}) => {
  const [importData, setImportData] = useState<FlowData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleFileUpload = useCallback((file: File) => {
    setIsValidating(true);
    setError(null);
    setImportData(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = typeof e.target?.result === "string" ? e.target.result : "";
        const parsed = JSON.parse(content);
        
        // Validate the structure - only require data field
        if (!parsed.data) {
          throw new Error("Invalid flow file format. Missing required 'data' field.");
        }
        
        if (!parsed.data.nodes || !Array.isArray(parsed.data.nodes)) {
          throw new Error("Invalid flow file format. Missing or invalid nodes data.");
        }
        
        if (!parsed.data.edges || !Array.isArray(parsed.data.edges)) {
          throw new Error("Invalid flow file format. Missing or invalid edges data.");
        }
        
        // Validate node structure
        for (const node of parsed.data.nodes) {
          if (!node.id || !node.data) {
            throw new Error("Invalid node structure in flow file.");
          }
        }
        
        // Generate version and timestamp if missing
        const flowData: FlowData = {
          version: parsed.version || "1.0.0",
          timestamp: parsed.timestamp || new Date().toISOString(),
          metadata: {
            ...(parsed.metadata ?? {}),
            status: parsed.metadata?.status || "draft"
          },
          data: {
            nodes: parsed.data.nodes || [],
            edges: parsed.data.edges || [],
            globalVariableRegistry: parsed.data.globalVariableRegistry || {},
            decisionVariablesRegistry: parsed.data.decisionVariablesRegistry || {},
            identifiers: parsed.data.identifiers || [],
            interjectionGroups: parsed.data.interjectionGroups || [],
            nodeIdCounter: parsed.data.nodeIdCounter || 0,
            isFirstNode: parsed.data.isFirstNode || false,
            systemPrompt: parsed.data.systemPrompt
          }
        };
        
        setImportData(flowData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to parse flow file");
        setImportData(null);
      } finally {
        setIsValidating(false);
      }
    };
    
    reader.onerror = () => {
      setError("Failed to read file");
      setIsValidating(false);
    };
    
    reader.readAsText(file);
    return false; // Prevent default upload behavior
  }, []);

  const handleImport = () => {
    if (importData) {
      onImport(importData);
      handleCancel();
    }
  };

  const handleCancel = () => {
    setImportData(null);
    setError(null);
    setIsValidating(false);
    onCancel();
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".json",
    beforeUpload: handleFileUpload,
    showUploadList: false,
  };

  return (
    <Dialog open={visible} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Import Conversation Flow
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="text-base font-gc-med text-foreground">Upload Flow JSON File</h4>
            <p className="text-sm text-muted-foreground">
              Select a conversation flow JSON file to import. This will replace your current flow. Version and timestamp will be generated automatically if not provided.
            </p>
          </div>

          <Card className="border-2 border-dashed border-border hover:border-muted-foreground/50 transition-colors">
            <CardContent className="p-8">
              <div 
                className="text-center cursor-pointer"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.json';
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) handleFileUpload(file);
                  };
                  input.click();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) handleFileUpload(file);
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="flex flex-col items-center space-y-4">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <div className="space-y-2">
                    <p className="text-base font-gc-reg text-foreground">
                      Click or drag flow JSON file to this area to upload
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Only .json files are supported. The file should contain valid conversation flow data with nodes and edges.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                <strong>Import Error:</strong> {error}
              </AlertDescription>
            </Alert>
          )}

          {importData && !error && (
            <Alert className="border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                <div className="space-y-2">
                  <div className="font-medium">Flow Ready to Import</div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span>{importData.data.nodes.length} nodes, {importData.data.edges.length} edges</span>
                  </div>
                  <div className="text-sm text-green-600">
                    Version: {importData.version} | 
                    Timestamp: {new Date(importData.timestamp).toLocaleString()} |
                    Status: {importData.metadata?.status || "unknown"}
                  </div>
                  {importData.metadata?.title && (
                    <div className="text-sm">
                      <strong>Title:</strong> {importData.metadata.title}
                    </div>
                  )}
                  {importData.metadata?.description && (
                    <div className="text-sm">
                      <strong>Description:</strong> {importData.metadata.description}
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!importData || isValidating}
            className={isValidating ? "opacity-50" : ""}
          >
            {isValidating ? "Validating..." : "Import Flow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFlowModal;